import React, { useEffect, useState, useCallback } from "react";
import AffiliateLayout from "@/components/AffiliateLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  LinkIcon, Plus, TrendingUp, Copy, Check, X, ExternalLink, Trash2,
  DollarSign, MousePointerClick, Percent, Wallet,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function fmtMxn(n) { return `$${(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtNum(n) { return (n || 0).toLocaleString("es-MX"); }

export default function AffiliateDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [links, setLinks] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l, c] = await Promise.all([
        api.get("/affiliate/dashboard"),
        api.get("/affiliate/links"),
        api.get("/affiliate/commissions?limit=10"),
      ]);
      setSummary(s.data);
      setLinks(l.data.items || []);
      setCommissions(c.data.items || []);
    } catch (e) { toast.error("Error al cargar dashboard"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shortUrl = (short_code) => `${BACKEND_URL}/api/r/${short_code}`;

  const copy = (short_code) => {
    navigator.clipboard.writeText(shortUrl(short_code));
    setCopied(short_code);
    setTimeout(() => setCopied(null), 1500);
    toast.success("Copiado");
  };

  const delLink = async (id) => {
    if (!window.confirm("¿Eliminar este link? Los clicks históricos se conservan.")) return;
    try { await api.delete(`/affiliate/links/${id}`); toast.success("Link desactivado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!summary) return <AffiliateLayout user={user}><div className="p-6 text-gray-500">Cargando…</div></AffiliateLayout>;

  return (
    <AffiliateLayout user={user}>
      <div data-testid="affiliate-dashboard">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">Fase 14.4 · Programa afiliados</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">
              Hola, {user?.full_name?.split(" ")[0] || "afiliado"}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Rol: <span className="text-brand-orange-500 font-semibold">{(user?.roles || []).find((r) => r === "reseller" || r === "influencer") || "afiliado"}</span>
            </p>
          </div>
          <button onClick={() => setShowNew(true)}
            data-testid="new-link-btn"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold">
            <Plus className="w-4 h-4" /> Nuevo link
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Kpi label="Clicks 30d" value={fmtNum(summary.clicks_30d)} icon={MousePointerClick} testid="kpi-clicks" />
          <Kpi label="Conversions 30d" value={fmtNum(summary.conversions_30d)} sub={`${summary.conversion_rate_pct}% CR`} icon={Percent} testid="kpi-conversions" />
          <Kpi label="Comisiones pendientes" value={fmtMxn(summary.commissions_pending_mxn)} icon={DollarSign} testid="kpi-pending" accent />
          <Kpi label="Total pagado" value={fmtMxn(summary.commissions_paid_mxn)} sub={`${fmtMxn(summary.commissions_approved_mxn)} approved`} icon={Wallet} testid="kpi-paid" />
        </div>

        {/* Chart */}
        <div className="bg-[#111827] border border-gray-800 rounded-xl p-4 mb-6">
          <p className="text-sm font-semibold text-white mb-2">Clicks por día · últimos 30d</p>
          <div className="h-52">
            {(summary.click_trend_by_day || []).length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">Sin clicks aún</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.click_trend_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: 10 }} />
                  <YAxis stroke="#6B7280" style={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 8 }} labelStyle={{ color: "#DC5A0F" }} />
                  <Line type="monotone" dataKey="clicks" stroke="#DC5A0F" strokeWidth={2} dot={false} name="Total" />
                  <Line type="monotone" dataKey="unique" stroke="#F97316" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Únicos" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Links table */}
        <div className="mb-6">
          <p className="text-sm font-semibold text-white mb-2">Mis links ({links.length})</p>
          <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="affiliate-links-table">
            <table className="w-full">
              <thead className="bg-[#0B0F14] border-b border-gray-800">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Short URL</th>
                  <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Campaña</th>
                  <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Destino</th>
                  <th className="text-right px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Clicks</th>
                  <th className="text-right px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Conv</th>
                  <th className="text-right px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Revenue</th>
                  <th className="text-right px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {links.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm">Sin links todavía. Crea el primero para empezar a generar comisiones.</td></tr>
                ) : links.map((l) => (
                  <tr key={l.id} data-testid={`link-row-${l.short_code}`} className="hover:bg-[#0B0F14]">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-brand-orange-500">/api/r/{l.short_code}</span>
                        <button onClick={() => copy(l.short_code)}
                          data-testid={`link-copy-${l.short_code}`}
                          className="p-1 text-gray-400 hover:text-brand-orange-500">
                          {copied === l.short_code ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-white">{l.campaign_name}</td>
                    <td className="px-4 py-2 text-xs font-mono text-gray-400">{l.destination_url}</td>
                    <td className="px-4 py-2 text-right text-xs font-mono text-white">{l.clicks_total}</td>
                    <td className="px-4 py-2 text-right text-xs font-mono text-emerald-300">{l.conversions_total}</td>
                    <td className="px-4 py-2 text-right text-xs font-mono text-brand-orange-500">{fmtMxn(l.revenue_generated_mxn)}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <a href={shortUrl(l.short_code)} target="_blank" rel="noreferrer"
                          className="p-1 text-gray-400 hover:text-brand-orange-500"><ExternalLink className="w-3 h-3" /></a>
                        <button onClick={() => delLink(l.id)}
                          data-testid={`link-delete-${l.short_code}`}
                          className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent commissions */}
        <div>
          <p className="text-sm font-semibold text-white mb-2">Comisiones recientes ({commissions.length})</p>
          <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="affiliate-commissions-table">
            <table className="w-full">
              <thead className="bg-[#0B0F14] border-b border-gray-800">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Fecha</th>
                  <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Orden</th>
                  <th className="text-right px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Monto</th>
                  <th className="text-right px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Comisión</th>
                  <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {commissions.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500 text-sm">Aún sin comisiones.</td></tr>
                ) : commissions.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-2 text-xs text-gray-400 font-mono">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-2 text-xs font-mono text-white">{c.order_id || c.subscription_id || "—"}</td>
                    <td className="px-4 py-2 text-right text-xs font-mono text-gray-300">{fmtMxn(c.order_amount_mxn)}</td>
                    <td className="px-4 py-2 text-right text-xs font-mono text-brand-orange-500">{fmtMxn(c.commission_amount_mxn)}</td>
                    <td className="px-4 py-2"><CommissionStatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showNew && <NewLinkModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      </div>
    </AffiliateLayout>
  );
}

function Kpi({ label, value, sub, icon: Icon, testid, accent }) {
  return (
    <div data-testid={testid}
      className={`p-4 rounded-xl border ${accent ? "bg-brand-orange-500/10 border-brand-orange-500/40" : "bg-[#111827] border-gray-800"}`}>
      <div className="flex items-start justify-between">
        <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${accent ? "text-brand-orange-500" : "text-gray-500"}`} />}
      </div>
      <p className={`mt-2 text-2xl font-bold ${accent ? "text-brand-orange-500" : "text-white"} font-display`}>{value}</p>
      {sub && <p className="mt-1 text-[10px] text-gray-500">{sub}</p>}
    </div>
  );
}

function CommissionStatusBadge({ status }) {
  const cfg = {
    pending: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
    approved: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    paid: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    reversed: "bg-red-500/20 text-red-300 border-red-500/40",
  };
  return <span className={`inline-flex px-2 py-0.5 text-[10px] rounded border font-semibold uppercase tracking-widest ${cfg[status] || "bg-gray-700 text-gray-400 border-gray-700"}`}>{status}</span>;
}

function NewLinkModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ destination_url: "/catalogo", campaign_name: "", utm_source: "", utm_medium: "", utm_campaign: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const { data } = await api.post("/affiliate/links", form);
      toast.success(`Link creado: /api/r/${data.short_code}`);
      onCreated(data);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3"
        data-testid="new-link-modal">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-bold text-white font-display">Nuevo link</h3>
          <button onClick={onClose} type="button" className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Destino (path relativo)</label>
          <input required value={form.destination_url}
            onChange={(e) => setForm({ ...form, destination_url: e.target.value })}
            data-testid="new-link-destination"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white font-mono"
            placeholder="/catalogo/hpcm" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Campaña</label>
          <input value={form.campaign_name}
            onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
            data-testid="new-link-campaign"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
            placeholder="IG Ads Q1" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400">UTM source</label>
            <input value={form.utm_source} onChange={(e) => setForm({ ...form, utm_source: e.target.value })}
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white font-mono" placeholder="instagram" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400">UTM medium</label>
            <input value={form.utm_medium} onChange={(e) => setForm({ ...form, utm_medium: e.target.value })}
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white font-mono" placeholder="social" />
          </div>
        </div>
        <button type="submit" disabled={submitting || !form.destination_url}
          data-testid="new-link-submit"
          className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
          {submitting ? "Creando…" : "Crear link"}
        </button>
      </form>
    </div>
  );
}
